import {Emitter} from '../util/event'
import {VendorList} from '../service/vendor-list'
import {PublisherPurposesList} from '../service/publisher-purposes-list'
import CMPError from '../cmp-error'
import IAPI from './api-interface'
import debug, {DEBUG, ERROR, WARN} from '../util/debug'
import {isUndefined} from '../util/logic'

export interface ConsentObject {
  [id: string]: boolean
}

export interface CallbackData {
  gdprApplies: boolean
  hasGlobalScope: boolean
}

export interface VendorConsents extends CallbackData {
  metadata: string
  purposes: ConsentObject
  vendorConsents: ConsentObject
}

export interface VendorConsentData extends CallbackData {
  consentData: string
}

export interface PingResponse {
  cmpLoaded: boolean
  gdprAppliesGlobally: boolean
}

export type Command =
  'hasStorageAccess' |
  'getVendorConsents' |
  'getConsentData' |
  'getPublisherConsents' |
  'getVendorList' |
  'giveThanks' |
  'openSettings' |
  'onUpdate' |
  'onConsent' |
  'onDisset' |
  'onReset' |
  'ready' |
  'rendered' |
  'reset' |
  'hasChoiceBeenMade' |
  'hasConsentedToAll' |
  'consentToAll' |
  'dissentToAll' |
  'getVendorList' |
  'getPublisherPurposesList'

export type SimpleCallback = (_: null, success: boolean) => void
export type GetVendorConsentCallback = (consents: VendorConsents | {}, success: boolean) => void
export type GetConsentDataCallback = (consentData: VendorConsentData | null, success: boolean) => void
export type PingCallback = (response: PingResponse, success: boolean) => void
export type HasChoiceBeenMadeCallback = (hasChoiceBeenMade: boolean, success: boolean) => void
export type HasConsentedToAllCallback = (hasConsentedToAll: boolean, success: boolean) => void
export type HasStorageAccessCallback = (hasStorageAccess: boolean, success: boolean) => void
export type GetVendorListCallback = (vendorList: VendorList | null, success: boolean) => void
export type GetPublisherPurposesListCallback = (publisherPurposesList: PublisherPurposesList | null, success: boolean) => void
export type ReturnValue = null | VendorConsents | VendorConsentData | PingResponse | boolean | VendorList | PublisherPurposesList

export type Callback =
  SimpleCallback |
  GetVendorConsentCallback |
  GetConsentDataCallback |
  PingCallback |
  HasChoiceBeenMadeCallback |
  HasConsentedToAllCallback |
  GetVendorListCallback |
  GetPublisherPurposesListCallback

export type Parameter = undefined | null | string[] | string
export type QueueCommand = [Command, Parameter, Callback]

export interface Queue {
  push: (command: QueueCommand) => void
  [Symbol.iterator] (): IterableIterator<QueueCommand>
}

export interface CMPCaller {
  (command: Command, parameter: Parameter, callback: Callback): void
  a: Queue
}

export default abstract class API extends Emitter implements IAPI {
  public static iframeHandler ({data, origin, source}: MessageEvent) {
    if (!source) {
      return
    }

    const msgIsString = typeof data === 'string'

    // tslint:disable-next-line:variable-name
    let __cmpCall: {
      command?: Command,
      parameter?: Parameter,
      callId?: string
    } = {}

    // tslint:disable-next-line:variable-name
    let __cmpReturn

    try {
      ({__cmpCall, __cmpReturn} = msgIsString ? JSON.parse(data) : data)
    } catch (error) {
      debug(error, WARN)
      debug(data, WARN)
      return
    }

    if (__cmpReturn) {
      return
    } else if (!__cmpCall) {
      debug('no __cmpCall', WARN)
      return
    }

    const {command, callId} = __cmpCall
    let {parameter = null} = __cmpCall

    if (isUndefined(command) || isUndefined(callId)) {
      throw new CMPError(`Missing required keys from iframe messge. Got: ${Object.keys(__cmpCall).join(', ')}`)
    }

    if (['consentToAll', 'dissentToAll'].includes(command) && parameter === null) {
      parameter = origin
    }

    const callback: Callback = (returnValue: any, success: boolean) => {
      const message = {
        __cmpReturn: {
          callId,
          returnValue,
          success
        }
      }

      const data = msgIsString ? JSON.stringify(message) : message

      debug(`[${callId}] [${this.name}] responding to source`)
      debug(data)
      source.postMessage(data, '*')
    }

    debug(`[${callId}] [${this.name}] received call ${command}(${parameter})`)
    window.__cmp(command, parameter, callback)
  }

  public static provision () {
    window.__cmp = window.__cmp || this.provisional
    window.__cmp.a = window.__cmp.a || []
  }

  private static provisional (command: Command, parameter: Parameter, callback: Callback): void {
    window.__cmp.a.push([command, parameter, callback])
  }

  public a: Queue | QueueCommand[]

  protected constructor (public baseURL: string) {
    super()
    this.a = {
      push: this.callQueueCommand.bind(this),
      *[Symbol.iterator] (): IterableIterator<QueueCommand> {}
    }
  }

  public abstract call (command: Command, parameter: Parameter, callback: Callback): void

  public callP (command: Command, parameter: Parameter = null): Promise<ReturnValue> {
    return new Promise((resolve, reject) => {
      this.call(command, parameter, (rtnValue: ReturnValue, success: boolean) => {
        if (success) {
          resolve(rtnValue)
        } else {
          reject(new CMPError('Bad response'))
        }
      })
    })
  }

  public callQueueCommand ([command, parameter, callback]: [Command, Parameter, Callback]) {
    this.call(command, parameter, callback)
  }

  public abstract consentToAll (origin: string): void

  public abstract dissentToAll (origin: string): void

  public callQueue () {
    API.provision()

    // @ts-ignore
    for (const queueCommand of window.__cmp.a) {
      this.callQueueCommand(queueCommand)
    }

    window.__cmp = this.call.bind(this)
    window.__cmp.a = this.a
  }
}
